/**
 * D1 Store Enhancements
 * Premium UI enhancements for the D1 Store theme
 * 
 * Features:
 * - Smooth scroll to anchors
 * - Lazy animation on scroll
 * - Touch-friendly carousels
 * - Variant change event dispatching
 */

(function() {
  'use strict';

  /**
   * Smooth Scroll to Anchors
   * Adds smooth scrolling behavior to anchor links
   */
  function initSmoothScroll() {
    const getScrollTarget = (hash) => {
      if (!hash || hash === '#' || hash === '#0') return null;

      try {
        return document.querySelector(hash);
      } catch (error) {
        return null;
      }
    };

    const scrollToHash = (hash, updateHistory = false) => {
      const target = getScrollTarget(hash);
      if (!target) return false;

      const headerHeight = document.querySelector('.header-wrapper')?.offsetHeight || 0;
      const targetPosition = target.getBoundingClientRect().top + window.pageYOffset - headerHeight - 20;

      window.scrollTo({
        top: Math.max(targetPosition, 0),
        behavior: 'smooth'
      });

      if (updateHistory && history.pushState) {
        history.pushState(null, '', hash);
      }

      return true;
    };

    document.addEventListener('click', function(e) {
      const link = e.target.closest('a[href*="#"]');
      if (!link) return;

      const href = link.getAttribute('href');
      if (!href) return;

      const url = new URL(href, window.location.href);
      const samePage =
        url.origin === window.location.origin &&
        url.pathname === window.location.pathname &&
        url.search === window.location.search;

      if (!samePage || !url.hash) return;

      if (!getScrollTarget(url.hash)) return;

      e.preventDefault();
      scrollToHash(url.hash, true);
    });

    const tryInitialHashScroll = () => {
      if (!window.location.hash) return;

      if (scrollToHash(window.location.hash, false)) return;

      window.setTimeout(() => {
        scrollToHash(window.location.hash, false);
      }, 250);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', tryInitialHashScroll, { once: true });
    } else {
      tryInitialHashScroll();
    }

    window.addEventListener('hashchange', function() {
      scrollToHash(window.location.hash, false);
    });
  }

  /**
   * Scroll Reveal Animations
   * Adds fade-in animations to elements as they enter viewport
   */
  function initScrollReveal() {
    // Check for reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        }
      });
    }, {
      root: null,
      rootMargin: '0px 0px -10% 0px',
      threshold: 0.1
    });

    const observeElements = () => {
      const revealElements = document.querySelectorAll('[data-reveal]:not(.is-revealed)');
      revealElements.forEach(el => {
        const rect = el.getBoundingClientRect();
        if (rect.width > 0 && rect.bottom > 0 && rect.top < window.innerHeight * 0.9) {
          el.classList.add('is-revealed');
          return;
        }
        observer.observe(el);
      });
    };

    observeElements();
    document.documentElement.classList.add('d1-reveal-ready');
    document.addEventListener('product-info:loaded', observeElements);

    // Re-observe on AJAX content updates (predictive results are injected later)
    const containers = ['ProductGridContainer', 'CartDrawer-CartItems'];
    containers.forEach(id => {
      const el = document.getElementById(id);
      if (el) {
        const mutationObserver = new MutationObserver(() => observeElements());
        mutationObserver.observe(el, { childList: true, subtree: true });
      }
    });
    document.querySelectorAll('predictive-search, [data-predictive-search]').forEach((el) => {
      const mutationObserver = new MutationObserver(() => observeElements());
      mutationObserver.observe(el, { childList: true, subtree: true });
    });

    // Handle Mega Menu opening
    document.querySelectorAll('.mega-menu').forEach(details => {
      details.addEventListener('toggle', () => {
        if (details.open) {
          observeElements();
        }
      });
    });

    // Handle Mobile Menu Drawer opening
    const menuDrawer = document.getElementById('Details-menu-drawer-container');
    if (menuDrawer) {
      menuDrawer.addEventListener('toggle', () => {
        if (menuDrawer.open) {
          observeElements();
        }
      });
    }

    // Handle Submenu navigations in mobile drawer
    document.querySelectorAll('.menu-drawer details').forEach(details => {
      details.addEventListener('toggle', () => {
        if (details.open) {
          observeElements();
        }
      });
    });
  }

  /**
   * Touch-Friendly Carousels
   * Adds swipe support to horizontal scroll containers
   */
  function initTouchCarousels() {
    const carousels = document.querySelectorAll('.slider, .carousel, [data-swipe]');
    
    carousels.forEach(carousel => {
      let startX = 0;
      let scrollLeft = 0;
      let isDown = false;
      let walk = 0;

      carousel.addEventListener('touchstart', (e) => {
        startX = e.touches[0].pageX;
        scrollLeft = carousel.scrollLeft;
      }, { passive: true });

      carousel.addEventListener('touchmove', (e) => {
        if (!startX) return;
        const x = e.touches[0].pageX;
        walk = (startX - x) * 1.5;
        carousel.scrollLeft = scrollLeft + walk;
      }, { passive: true });

      carousel.addEventListener('touchend', () => {
        startX = 0;
      }, { passive: true });

      // Mouse drag support for desktop
      carousel.addEventListener('mousedown', (e) => {
        isDown = true;
        carousel.classList.add('is-grabbing');
        startX = e.pageX - carousel.offsetLeft;
        scrollLeft = carousel.scrollLeft;
      });

      carousel.addEventListener('mouseleave', () => {
        isDown = false;
        carousel.classList.remove('is-grabbing');
      });

      carousel.addEventListener('mouseup', () => {
        isDown = false;
        carousel.classList.remove('is-grabbing');
      });

      carousel.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - carousel.offsetLeft;
        walk = (startX - x) * 2;
        carousel.scrollLeft = scrollLeft + walk;
      });
    });

    // Add grabbing cursor styles
    const grabStyles = document.createElement('style');
    grabStyles.textContent = `
      .slider, .carousel, [data-swipe] {
        cursor: grab;
        -webkit-overflow-scrolling: touch;
        scroll-snap-type: x mandatory;
      }
      .slider.is-grabbing, .carousel.is-grabbing, [data-swipe].is-grabbing {
        cursor: grabbing;
        scroll-snap-type: none;
      }
      .slider > *, .carousel > *, [data-swipe] > * {
        scroll-snap-align: start;
      }
    `;
    document.head.appendChild(grabStyles);
  }

  /**
   * Variant Change Event Dispatcher
   * Dispatches custom events when product variants change
   * Used by sticky buy bar and other components to sync
   */
  function initVariantSync() {
    // Watch for Dawn's variant changes
    document.addEventListener('change', (e) => {
      const input = e.target;
      
      // Check if it's a variant selector
      if (input.name === 'id' || input.closest('.product-form__input')) {
        // Find the product form
        const form = input.closest('form') || document.querySelector('#product-form-' + document.querySelector('product-info')?.dataset.section);
        if (!form) return;

        // Get variant ID
        const variantId = form.querySelector('input[name="id"]')?.value;
        if (!variantId) return;

        // Dispatch custom event
        document.dispatchEvent(new CustomEvent('variant:change', {
          detail: {
            variantId: parseInt(variantId),
            form: form,
            variant: input.variant || null // Pass variant object if available
          }
        }));
      }
    });

    // Also watch for variant picker button clicks
    document.addEventListener('click', (e) => {
      const button = e.target.closest('.product-form__input input[type="radio"]');
      if (button) {
        // Small delay to let Dawn update the variant ID
        setTimeout(() => {
          const form = button.closest('form');
          if (!form) return;

          const variantId = form.querySelector('input[name="id"]')?.value;
          if (!variantId) return;

          document.dispatchEvent(new CustomEvent('variant:change', {
            detail: {
              variantId: parseInt(variantId),
              form: form
            }
          }));
        }, 50);
      }
    });
  }

  /**
   * Preserve Scroll Position on Variant Change
   *
   * When a product uses a combined listing (each option maps to a different
   * product URL), Dawn's product-info swaps the whole <main> and the page
   * jumps to the top — it feels like a full refresh. This captures the scroll
   * position when a variant input changes and restores it once product-info
   * re-renders (it dispatches 'product-info:loaded' after both the full-page
   * swap and the lighter product-info swap).
   */
  function initVariantScrollPreserve() {
    let savedScrollY = null;
    let savedAt = 0;
    const MAX_AGE = 5000;

    // Capture in the capture phase so we record the position at the moment of
    // interaction, before any re-render can shift the layout.
    document.addEventListener(
      'change',
      (e) => {
        const input = e.target;
        if (!input) return;

        const isVariantSelector =
          input.closest?.('variant-selects') ||
          input.name === 'id' ||
          input.hasAttribute?.('data-d1-sticky-variant-select') ||
          input.closest?.('.d1-sticky-atc') ||
          input.closest?.('.sticky-buy-bar') ||
          input.closest?.('.product-form__input');

        if (!isVariantSelector) return;

        savedScrollY = window.scrollY;
        savedAt = Date.now();
      },
      true
    );

    document.addEventListener('product-info:loaded', () => {
      if (savedScrollY === null) return;

      const target = savedScrollY;
      savedScrollY = null;

      // Ignore stale captures (e.g. the initial page-load dispatch).
      if (Date.now() - savedAt > MAX_AGE) return;

      // Restore after layout settles. Multiple passes guard against late
      // reflows (images, fonts) nudging the document height.
      const restore = () => window.scrollTo({ top: target, behavior: 'auto' });
      requestAnimationFrame(() => {
        restore();
        requestAnimationFrame(restore);
      });
      window.setTimeout(restore, 120);
    });
  }

  /**
   * Image Lazy Loading Enhancement
   * Adds blur-up effect for lazy loaded images
   */
  function initLazyImageBlur() {
    // Check for reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    // Add blur-up styles
    const blurStyles = document.createElement('style');
    blurStyles.textContent = `
      img[loading="lazy"] {
        filter: blur(0);
        transition: filter 0.3s ease;
      }
      img[loading="lazy"]:not([src]) {
        filter: blur(10px);
      }
    `;
    document.head.appendChild(blurStyles);
  }

  /**
   * Header Scroll Behavior
   * Adds class when page is scrolled for styling
   */
  function initHeaderScroll() {
    const header = document.querySelector('.header-wrapper');
    if (!header) return;

    let lastScroll = 0;
    let ticking = false;

    function updateHeader() {
      const scroll = window.pageYOffset;
      
      // Add scrolled class
      if (scroll > 50) {
        header.classList.add('is-scrolled');
      } else {
        header.classList.remove('is-scrolled');
      }

      // Detect scroll direction
      if (scroll > lastScroll && scroll > 200) {
        header.classList.add('is-scroll-down');
        header.classList.remove('is-scroll-up');
      } else {
        header.classList.remove('is-scroll-down');
        header.classList.add('is-scroll-up');
      }

      lastScroll = scroll;
      ticking = false;
    }

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(updateHeader);
        ticking = true;
      }
    }, { passive: true });
  }

  /**
   * Recently Viewed Products
   * Stores product views in localStorage and renders them in the search drawer.
   */
  function initRecentlyViewedProducts() {
    const storageKey = 'd1:recently-viewed-products';
    const maxItems = 6;

    const readItems = () => {
      try {
        const items = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
        return Array.isArray(items) ? items.filter((item) => item && item.handle && item.url && item.title) : [];
      } catch (_) {
        return [];
      }
    };

    const writeItems = (items) => {
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(items.slice(0, maxItems)));
      } catch (_) {}
    };

    const currentProduct = window.d1CurrentProduct;
    if (currentProduct && currentProduct.handle && currentProduct.url && currentProduct.title) {
      const nextItems = [
        {
          handle: currentProduct.handle,
          title: currentProduct.title,
          url: currentProduct.url,
          image: currentProduct.image || '',
          price: currentProduct.price || '',
          variantId: currentProduct.variantId || null,
          available: currentProduct.available !== false,
          viewedAt: Date.now(),
        },
        ...readItems().filter((item) => item.handle !== currentProduct.handle),
      ];
      writeItems(nextItems);
    }

    // ponytail: resolve first available variant when older localStorage rows lack variantId
    const resolveVariantId = async (item) => {
      if (item.variantId) return item.variantId;
      if (!item.handle) return null;
      try {
        const res = await fetch(`/products/${item.handle}.js`);
        if (!res.ok) return null;
        const product = await res.json();
        const variant =
          (product.variants || []).find((v) => v.available) || (product.variants || [])[0];
        if (!variant) return null;
        item.variantId = variant.id;
        item.available = !!variant.available;
        const items = readItems().map((row) =>
          row.handle === item.handle
            ? { ...row, variantId: item.variantId, available: item.available }
            : row
        );
        writeItems(items);
        return item.variantId;
      } catch (_) {
        return null;
      }
    };

    const refreshCartAfterAdd = async () => {
      try {
        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();
        document.querySelectorAll('.cart-count-bubble span:not(.visually-hidden)').forEach((el) => {
          el.textContent = cart.item_count;
        });
        document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
          bubble.classList.toggle('hidden', cart.item_count === 0);
        });

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          const secs = cartDrawer.getSectionsToRender
            ? cartDrawer.getSectionsToRender().map((s) => s.id)
            : ['cart-drawer', 'cart-icon-bubble'];
          const secRes = await fetch(`${window.location.pathname}?sections=${secs.join(',')}`);
          if (secRes.ok) {
            cartDrawer.classList.remove('is-empty');
            cartDrawer.renderContents({ sections: await secRes.json(), id: null });
            if (typeof cartDrawer.open === 'function') cartDrawer.open();
          }
        }
      } catch (_) {}
    };

    const closeSearchDrawer = () => {
      document.querySelectorAll('.header__search--drawer details[open]').forEach((details) => {
        details.removeAttribute('open');
      });
    };

    const renderContainers = () => {
      const items = readItems();
      document.querySelectorAll('[data-recently-viewed]').forEach((container) => {
        const list = container.querySelector('[data-recently-viewed-list]');
        if (!list) return;

        list.innerHTML = '';
        if (!items.length) {
          container.hidden = true;
          return;
        }

        items.forEach((item) => {
          const listItem = document.createElement('li');
          listItem.className = 'search-drawer__recently-viewed-item';

          const link = document.createElement('a');
          const media = document.createElement('span');
          const content = document.createElement('span');
          const title = document.createElement('span');

          link.className = 'search-drawer__recently-viewed-link';
          link.href = item.url;

          media.className = 'search-drawer__recently-viewed-media';
          if (item.image) {
            const img = document.createElement('img');
            img.src = item.image;
            img.alt = item.title;
            img.loading = 'lazy';
            media.appendChild(img);
          } else {
            media.innerHTML =
              '<svg class="search-drawer__recently-viewed-placeholder" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>';
          }

          title.className = 'search-drawer__recently-viewed-title';
          title.textContent = item.title;
          content.appendChild(title);

          if (item.price) {
            const price = document.createElement('span');
            price.className = 'search-drawer__recently-viewed-price';
            price.textContent = item.price;
            content.appendChild(price);
          }

          link.append(media, content);
          listItem.appendChild(link);

          if (item.available !== false) {
            const addBtn = document.createElement('button');
            addBtn.type = 'button';
            addBtn.className = 'search-drawer__add search-drawer__recently-viewed-add';
            addBtn.setAttribute('aria-label', `Add ${item.title} to cart`);
            if (item.variantId) addBtn.dataset.variantId = String(item.variantId);
            addBtn.dataset.handle = item.handle;
            addBtn.innerHTML =
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>';
            listItem.appendChild(addBtn);
          }

          list.appendChild(listItem);
        });

        container.hidden = false;
      });
    };

    document.addEventListener('mousedown', (event) => {
      const button = event.target.closest('.search-drawer__add');
      // Keep predictive-search open: prevent input blur before click lands
      if (button && button.closest('predictive-search')) event.preventDefault();
    });

    document.addEventListener('click', async (event) => {
      const button = event.target.closest('.search-drawer__add');
      if (!button) return;

      event.preventDefault();
      event.stopPropagation();
      if (button.classList.contains('is-loading')) return;

      button.classList.add('is-loading');
      button.disabled = true;

      try {
        let variantId = button.dataset.variantId
          ? parseInt(button.dataset.variantId, 10)
          : null;
        if (!variantId) {
          variantId = await resolveVariantId({
            handle: button.dataset.handle,
            variantId: null,
          });
          if (variantId) button.dataset.variantId = String(variantId);
        }
        if (!variantId) throw new Error('Missing variant');

        const res = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        });
        if (!res.ok) throw new Error('Add failed');
        await res.json();

        closeSearchDrawer();
        await refreshCartAfterAdd();
      } catch (error) {
        console.error('Search drawer add to cart failed:', error);
      } finally {
        button.classList.remove('is-loading');
        button.disabled = false;
      }
    });

    renderContainers();
    document.querySelectorAll('.header__search--drawer details').forEach((details) => {
      details.addEventListener('toggle', () => {
        setSearchTawkVisible(!details.open);
        if (!details.open) return;

        renderContainers();
        if (
          document.querySelector('[data-d1-mobile-search]') &&
          window.matchMedia('(max-width: 749px)').matches
        ) {
          return;
        }
        // Clear + focus so the drawer opens ready to type
        const input =
          details.querySelector('.aa-Input') || details.querySelector('#Search-In-Modal, input[type="search"]');
        if (input && !input.disabled) {
          input.value = '';
          input.dispatchEvent(new Event('input', { bubbles: true }));
          requestAnimationFrame(() => input.focus({ preventScroll: true }));
        }
      });
    });
  }

  // ponytail: custom launcher is the bubble — overlays only close an open chat
  function setSearchTawkVisible(visible) {
    try {
      if (visible) return;
      if (window.Tawk_API && typeof window.Tawk_API.hideWidget === 'function') {
        window.Tawk_API.hideWidget();
      }
    } catch (_) {}
  }

  /**
   * Floating scroll-to-top (glass button, right stack above Tawk / sticky ATC)
   */
  function initScrollTop() {
    const btn = document.querySelector('[data-d1-scroll-top]');
    if (!btn) return;

    const threshold = 400;
    let ticking = false;

    const update = () => {
      btn.classList.toggle('is-visible', window.scrollY > threshold);
      ticking = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      },
      { passive: true }
    );

    btn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });

    update();
  }

  function initD1GalleryNav() {
    document.querySelectorAll('media-gallery.d1-gallery').forEach((gallery) => {
      if (gallery.dataset.d1NavBound === 'true') return;
      gallery.dataset.d1NavBound = 'true';
      gallery.querySelectorAll('[data-d1-gallery-step]').forEach((button) => {
        button.addEventListener('click', () => {
          const thumbs = Array.from(gallery.querySelectorAll('[id^="GalleryThumbnails"] [data-target]')).filter(
            (thumb) => {
              if (getComputedStyle(thumb).display === 'none') return false;
              return gallery.querySelector(`[id^="GalleryViewer"] [data-media-id="${thumb.dataset.target}"]`);
            }
          );
          if (thumbs.length < 2) return;
          const current = thumbs.findIndex((thumb) => thumb.querySelector('button[aria-current]'));
          const index = current < 0 ? 0 : current;
          const nextIndex =
            button.dataset.d1GalleryStep === 'next'
              ? (index + 1) % thumbs.length
              : (index - 1 + thumbs.length) % thumbs.length;
          if (typeof gallery.setActiveMedia === 'function') {
            gallery.setActiveMedia(thumbs[nextIndex].dataset.target, false);
          }
        });
      });
    });
  }

  /**
   * Initialize all enhancements
   */
  function parkBundleModals() {
    document.querySelectorAll('.d1-bundle-quickview-modal').forEach((modal) => {
      if (modal.parentElement !== document.body) {
        document.body.appendChild(modal);
      }
    });
  }

  function closeGiftModal(modal) {
    if (!modal) return;
    modal.setAttribute('aria-hidden', 'true');
    modal.classList.remove('is-open');
    if (!document.querySelector('.d1-bundle-quickview-modal.is-open')) {
      document.body.classList.remove('d1-modal-open');
    }
  }

  function openGiftModal(modal) {
    if (!modal) return;
    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }
    modal.setAttribute('aria-hidden', 'false');
    modal.classList.add('is-open');
    document.body.classList.add('d1-modal-open');
    modal.dataset.d1OpenedAt = String(Date.now());
  }

  function modalFromOpenTrigger(trigger) {
    const modalId = trigger && trigger.getAttribute('data-d1-bundle-modal-open');
    return modalId ? document.getElementById(modalId) : null;
  }

  function wasJustOpened(modal) {
    const openedAt = Number(modal && modal.dataset.d1OpenedAt);
    return Number.isFinite(openedAt) && Date.now() - openedAt < 350;
  }

  function initGiftModals() {
    if (document.documentElement.dataset.d1GiftModals === '1') return;
    document.documentElement.dataset.d1GiftModals = '1';
    parkBundleModals();

    document.addEventListener('click', (event) => {
      const openTrigger = event.target.closest('[data-d1-bundle-modal-open]');
      if (openTrigger) {
        const modal = modalFromOpenTrigger(openTrigger);
        if (!modal) return;
        event.preventDefault();
        event.stopPropagation();
        openGiftModal(modal);
        return;
      }

      const closeTrigger = event.target.closest('[data-d1-bundle-modal-close]');
      if (closeTrigger) {
        const modal =
          closeTrigger.closest('.d1-bundle-quickview-modal') ||
          document.querySelector('.d1-bundle-quickview-modal.is-open');
        if (wasJustOpened(modal)) return;
        event.preventDefault();
        closeGiftModal(modal);
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      closeGiftModal(document.querySelector('.d1-bundle-quickview-modal.is-open'));
    });
  }

  function init() {
    initSmoothScroll();
    initScrollReveal();
    initTouchCarousels();
    initVariantSync();
    initVariantScrollPreserve();
    initLazyImageBlur();
    initHeaderScroll();
    initRecentlyViewedProducts();
    initScrollTop();
    initD1GalleryNav();
    initGiftModals();
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
